/**
 * Hardware Page
 *
 * Stripe Terminal readers and accessories Coupling provides to merchants.
 * Catalog data lives in src/content/hardware.json.
 */
import React from 'react';
import { Badge, Footer, SEO, breadcrumbSchema } from '@coupling/ui';
import {
  AccessoryCard,
  ReaderCard,
  ReaderComparisonTable,
  TRADEMARK_DISCLAIMER,
  HARDWARE_FRAMING,
  accessories,
  readers,
} from '@coupling/ui/hardware';
import Header from '../components/Header';
import { seoConfig } from '../config/seo';

const DESCRIPTION =
  'Tap to Pay on your phone, or an optional Stripe Terminal reader from Coupling. Compare the T600, S710, S700, M2, and WisePOS E, plus docks, hubs, and chargers.';

export const Hardware: React.FC = () => (
  <div className="min-h-screen bg-white">
    <SEO
      title={`POS Hardware - ${seoConfig.siteName}`}
      description={DESCRIPTION}
      canonical={`${seoConfig.siteUrl}/hardware`}
      ogImage={seoConfig.defaultOgImage}
      ogUrl={`${seoConfig.siteUrl}/hardware`}
      jsonLd={[
        breadcrumbSchema([
          { name: 'Home', url: seoConfig.siteUrl },
          { name: 'Hardware', url: `${seoConfig.siteUrl}/hardware` },
        ]),
      ]}
    />
    <Header />

    <section className="bg-gradient-to-br from-brand-primary via-brand-primary-dark to-brand-accent px-4 pb-16 pt-32 text-white">
      <div className="mx-auto max-w-4xl text-center">
        <Badge variant="secondary" size="md">Stripe Terminal</Badge>
        <h1 className="mt-4 font-display text-4xl font-bold sm:text-5xl">POS hardware</h1>
        <p className="mt-4 text-xl text-white/90">{HARDWARE_FRAMING}</p>
        <p className="mx-auto mt-4 max-w-2xl text-white/80">
          Every reader works through Coupling&apos;s POS integration with Stripe, so sales, tips, and rewards land in
          one place. The Stripe Reader M2 has no screen and pairs with our phone or tablet app.
        </p>
      </div>
    </section>

    <section className="px-4 py-16" aria-labelledby="readers-heading">
      <div className="mx-auto max-w-6xl">
        <h2 id="readers-heading" className="mb-8 font-display text-3xl font-bold text-neutral-900">Card readers</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {readers.map((reader) => <ReaderCard key={reader.id} reader={reader} />)}
        </div>
      </div>
    </section>

    <section className="bg-neutral-50 px-4 py-16" aria-labelledby="compare-heading">
      <div className="mx-auto max-w-6xl">
        <h2 id="compare-heading" className="mb-8 font-display text-3xl font-bold text-neutral-900">Compare readers</h2>
        <ReaderComparisonTable readers={readers} />
      </div>
    </section>

    <section className="px-4 py-16" aria-labelledby="accessories-heading">
      <div className="mx-auto max-w-6xl">
        <h2 id="accessories-heading" className="mb-8 font-display text-3xl font-bold text-neutral-900">Accessories</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {accessories.map((accessory) => <AccessoryCard key={accessory.id} accessory={accessory} />)}
        </div>
        <p className="mt-10 text-sm text-neutral-500">
          Prices shown are Stripe&apos;s US list prices before tax and shipping. Ask us about pricing for your shop.
        </p>
        <p className="mt-2 text-xs text-neutral-500">{TRADEMARK_DISCLAIMER}</p>
      </div>
    </section>

    <Footer />
  </div>
);

export default Hardware;
